<script setup lang="ts">
import { computed } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import Icon from "@/design/primitives/Icon.vue";
import type { NavigateTarget } from "../composables/use-turn-stream";
import { useAgentsI18n } from "../i18n/use-agents-i18n";

/**
 * O que a navegação deixou na conversa.
 *
 * Navegar é uma ação do agente como qualquer outra, e uma ação sem rastro é uma
 * ação que ninguém consegue desfazer: a rota muda, a tela vira outra e resta a
 * pergunta "o que foi que ele fez?". Um toast não serve — o `use-toast` atual não
 * tem botão de ação, só mensagem e fechar — e a doca vive FORA do `<RouterView>`
 * (`App.vue:217-221`), então este cartão continua na frente da pessoa depois que
 * a tela trocou. É por isso que ele mora na thread e não em cima dela.
 *
 * O botão depende de ONDE a pessoa está: se a rota já é o destino, o que falta é
 * voltar; se ninguém navegou (a `AgentsPage` e a `InboxPage` não escutam o
 * `navigate`, e navegar dali desmontaria a própria conversa), o que falta é o
 * link. O cartão decide isso sozinho em vez de receber mais uma prop.
 */
const props = defineProps<{ target: NavigateTarget }>();

const { t, te } = useAgentsI18n();
const route = useRoute();
const router = useRouter();

const here = computed(() => route.path === props.target.to);

/** `kind` is optional on the wire and can gain new values, so the label falls back
 *  to the name itself — and disappears when the server did not say where the
 *  destination came from. */
const kindLabel = computed(() => {
	const kind = props.target.kind;
	if (!kind) return "";
	return te(`navigate.${kind}`) ? t(`navigate.${kind}`) : kind;
});
</script>

<template>
	<div
		class="flex items-center gap-2 rounded-md border border-border bg-bg-subtle px-2.5 py-1.5 text-xs"
	>
		<Icon name="arrowUpRight" class="size-3.5 shrink-0" />
		<span class="min-w-0 flex-1 truncate">
			<span v-if="kindLabel" class="text-fg-subtle">{{ kindLabel }}</span>
			<span class="ml-1 text-fg">{{ target.label }}</span>
			<span v-if="target.reason" class="block truncate text-3xs text-fg-subtle">
				{{ target.reason }}
			</span>
		</span>
		<button
			v-if="here"
			type="button"
			class="shrink-0 text-fg-subtle underline hover:text-fg"
			@click="router.back()"
		>
			{{ t("navigate.back") }}
		</button>
		<RouterLink v-else :to="target.to" class="shrink-0 text-accent underline">{{ t("navigate.open") }}</RouterLink>
	</div>
</template>
